// Lecture de code-barres via l'API native `BarcodeDetector` (Chrome/Android — objectif § 1).
// Absente de `lib.dom.d.ts` de TypeScript (API encore non standard) : déclarée ici en ambient.
// `typeof window !== 'undefined'` rend ce module sûr à importer sous Node (tests, SSR) : il renvoie
// simplement « indisponible ».
import type { BarcodeDetection } from './types.js';

interface BarcodeDetectorResult {
  rawValue: string;
  format: string;
}

interface BarcodeDetectorInstance {
  detect(source: ImageBitmapSource): Promise<BarcodeDetectorResult[]>;
}

interface BarcodeDetectorConstructor {
  new (options?: { formats: string[] }): BarcodeDetectorInstance;
  getSupportedFormats(): Promise<string[]>;
}

declare global {
  interface Window {
    BarcodeDetector?: BarcodeDetectorConstructor;
  }
}

/** Formats EAN-13, EAN-8, UPC-A dans la nomenclature de la spec Shape Detection. */
const REQUIRED_FORMATS = ['ean_13', 'ean_8', 'upc_a'];

export function isNativeBarcodeDetectorAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.BarcodeDetector === 'function';
}

export type DetectFn = (source: ImageBitmapSource) => Promise<BarcodeDetection[]>;

/**
 * Construit un détecteur natif s'il est disponible et supporte au moins un des formats requis.
 * Renvoie `null` sinon (pas d'API, ou aucun format EAN/UPC supporté) : l'appelant bascule alors sur
 * ZXing (`zxingDetector.ts`).
 */
export async function createNativeDetector(): Promise<DetectFn | null> {
  if (!isNativeBarcodeDetectorAvailable()) return null;
  const Ctor = window.BarcodeDetector as BarcodeDetectorConstructor;
  try {
    const supported = await Ctor.getSupportedFormats();
    const formats = REQUIRED_FORMATS.filter((format) => supported.includes(format));
    if (formats.length === 0) return null;
    const detector = new Ctor({ formats });
    return (source) => detector.detect(source);
  } catch {
    return null;
  }
}
